import React, { useMemo } from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import ItemRow from "./ItemRow";
import { tokens, sharedStyles } from "../styles";

// A single section card inside the Crate-wise Packing tab — mirrors
// each {Current Crate / Crate: <id>} block on web's Customer Crate Items
// page. Pack Visible Items button packs that section's unpacked items;
// each row has Mark Issue via the shared MarkingSheet (parent owns the
// modal). For connecting crates, the backend already filters items to
// the current-crate customer list, so this component just renders
// what's given.

const CratePackingSection = ({
  section,
  isCurrent,
  packing,
  unpacking,
  onPack,
  onUnpack,
  onPressMark,
  isProcessing,
}) => {
  const items = useMemo(
    () => (Array.isArray(section?.items) ? section.items : []),
    [section],
  );
  const totalQty = items.reduce((s, it) => s + Number(it?.quantity || 0), 0);
  const packedQty = items
    .filter((it) => it?.packed_at)
    .reduce((s, it) => s + Number(it?.quantity || 0), 0);
  const unpackedQty = Math.max(0, totalQty - packedQty);
  const hasUnpacked = items.some((it) => !it?.packed_at && it?.order_item_id);
  const hasPacked = items.some(
    (it) => Boolean(it?.packed_at) && it?.order_item_id,
  );

  return (
    <View style={[sharedStyles.card, styles.card]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Text variant="caption_2" style={styles.kicker}>
            {isCurrent ? "CURRENT CRATE" : "CONNECTING CRATE"}
          </Text>
          <Text variant="title_16" style={styles.title} numberOfLines={1}>
            {section?.crate_id || section?.title || "—"}
          </Text>
          <Text variant="caption_2" style={styles.meta}>
            {packedQty}/{totalQty} packed · {unpackedQty} unpacked
          </Text>
        </View>
      </View>
      {items.length === 0 ? (
        <Text variant="caption_2" style={styles.empty}>
          No items in this section.
        </Text>
      ) : (
        <>
          <View style={styles.actionsRow}>
            {hasUnpacked ? (
              <TouchableOpacity
                activeOpacity={0.85}
                disabled={packing}
                onPress={() => onPack?.(section)}
                style={[
                  styles.actionBtn,
                  styles.actionPrimary,
                  packing && styles.actionDisabled,
                ]}
              >
                <Text variant="caption_2" style={styles.actionPrimaryText}>
                  Pack Visible Items
                </Text>
              </TouchableOpacity>
            ) : null}
            {hasPacked && isCurrent ? (
              <TouchableOpacity
                activeOpacity={0.85}
                disabled={unpacking}
                onPress={() => onUnpack?.(section)}
                style={[
                  styles.actionBtn,
                  styles.actionDanger,
                  unpacking && styles.actionDisabled,
                ]}
              >
                <Text variant="caption_2" style={styles.actionDangerText}>
                  Unpack
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.itemsList}>
            {items.map((it) => (
              <ItemRow
                key={String(it?.order_item_id || it?.hash || Math.random())}
                item={it}
                processing={
                  isProcessing ? isProcessing(it?.order_item_id) : false
                }
                onPressMark={(item) => onPressMark?.(item)}
              />
            ))}
          </View>
        </>
      )}
    </View>
  );
};

export default CratePackingSection;

const styles = StyleSheet.create({
  card: {
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.medium,
    paddingVertical: tokens.spacing.medium,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  headerLeft: { flex: 1 },
  kicker: {
    color: tokens.info,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.6,
  },
  title: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 15,
    marginTop: 2,
  },
  meta: { color: tokens.textSecondary, fontSize: 12, marginTop: 4 },
  empty: {
    color: tokens.textTertiary,
    fontSize: 12,
    textAlign: "center",
    paddingVertical: tokens.spacing.medium,
  },
  actionsRow: {
    flexDirection: "row",
    marginTop: tokens.spacing.medium,
    marginBottom: tokens.spacing.small,
  },
  actionBtn: {
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: 8,
    borderRadius: tokens.radius.md,
    marginRight: tokens.spacing.small,
  },
  actionPrimary: { backgroundColor: tokens.primary.bg },
  actionPrimaryText: {
    color: tokens.primary.fg,
    fontSize: 12,
    fontWeight: "700",
  },
  actionDanger: { backgroundColor: tokens.error },
  actionDangerText: { color: tokens.surface, fontSize: 12, fontWeight: "700" },
  actionDisabled: { opacity: 0.5 },
  itemsList: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: tokens.borderLight,
  },
});
